import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { getLogger } from '@packages/core/logging';
import { docsService } from '@web/services/singletons';
import { QuickReference } from '../../types';
import { QUICK_REFERENCE_TABS, QuickReferencePublicationType } from './quick-reference-tabs';

const logger = getLogger('quick-references');

export const QUICK_REFERENCE_PAGE_SIZE = 8;

// The backend mints downloadUrls with a 5 minute expiry and the dashboard outlives that, so
// anything older is re-signed at click time rather than handed to S3 and rejected.
const DOWNLOAD_URL_TTL_MS = 4 * 60 * 1000;

export type QuickReferenceTabStatus = 'idle' | 'loading' | 'loadingMore' | 'ready' | 'error';

export interface QuickReferenceTabState {
    documents: QuickReference[];
    lastLoadedPage: number;
    hasNext: boolean;
    totalElements: number;
    status: QuickReferenceTabStatus;
    fetchedAt: number;
}

type TabStateByType = Record<QuickReferencePublicationType, QuickReferenceTabState>;

const emptyTab: QuickReferenceTabState = {
    documents: [],
    lastLoadedPage: -1,
    hasNext: false,
    totalElements: 0,
    status: 'idle',
    fetchedAt: 0,
};

const initialState = (): TabStateByType =>
    QUICK_REFERENCE_TABS.reduce((acc, tab) => {
        acc[tab.publicationType] = { ...emptyTab, documents: [] };
        return acc;
    }, {} as TabStateByType);

type Action =
    | { type: 'reset' }
    | { type: 'request'; publicationType: QuickReferencePublicationType; append: boolean }
    | {
          type: 'success';
          publicationType: QuickReferencePublicationType;
          documents: QuickReference[];
          page: number;
          hasNext: boolean;
          totalElements: number;
          append: boolean;
      }
    | { type: 'failure'; publicationType: QuickReferencePublicationType }
    | {
          type: 'refreshUrl';
          publicationType: QuickReferencePublicationType;
          index: number;
          downloadUrl: string | null;
      };

const reducer = (state: TabStateByType, action: Action): TabStateByType => {
    if (action.type === 'reset') {
        return initialState();
    }

    const tab = state[action.publicationType];

    switch (action.type) {
        case 'request':
            return {
                ...state,
                [action.publicationType]: {
                    ...tab,
                    status: action.append ? 'loadingMore' : 'loading',
                },
            };
        case 'success':
            return {
                ...state,
                [action.publicationType]: {
                    documents: action.append ? [...tab.documents, ...action.documents] : action.documents,
                    lastLoadedPage: action.page,
                    hasNext: action.hasNext,
                    totalElements: action.totalElements,
                    status: 'ready',
                    fetchedAt: action.append ? tab.fetchedAt : Date.now(),
                },
            };
        case 'failure':
            return {
                ...state,
                [action.publicationType]: { ...tab, status: 'error' },
            };
        case 'refreshUrl': {
            if (!tab.documents[action.index]) {
                return state;
            }
            const documents = [...tab.documents];
            documents[action.index] = { ...documents[action.index], downloadUrl: action.downloadUrl };
            return {
                ...state,
                [action.publicationType]: { ...tab, documents },
            };
        }
        default:
            return state;
    }
};

export const useQuickReferences = ({
    activePublicationType,
    carlineId,
    modelYear,
}: {
    activePublicationType: QuickReferencePublicationType;
    carlineId: string | undefined;
    modelYear: number | undefined;
}) => {
    const [tabs, dispatch] = useReducer(reducer, undefined, initialState);
    // Per tab, so switching mid-load leaves the outgoing tab to finish rather than cancel.
    const requestsRef = useRef(new Map<QuickReferencePublicationType, AbortController>());

    const vehicleKey = carlineId && modelYear ? `${carlineId}:${modelYear}` : undefined;

    const abortAll = useCallback(() => {
        requestsRef.current.forEach((controller) => controller.abort());
        requestsRef.current.clear();
    }, []);

    useEffect(() => {
        abortAll();
        dispatch({ type: 'reset' });
    }, [vehicleKey, abortAll]);

    useEffect(() => {
        return abortAll;
    }, [abortAll]);

    const fetchPage = useCallback(
        async (publicationType: QuickReferencePublicationType, page: number, append: boolean) => {
            if (!carlineId || !modelYear) {
                return;
            }

            requestsRef.current.get(publicationType)?.abort();
            const controller = new AbortController();
            requestsRef.current.set(publicationType, controller);

            dispatch({ type: 'request', publicationType, append });

            try {
                const response = await docsService.getQuickReferences({
                    queryParameters: {
                        publicationType,
                        carlineId,
                        modelYear,
                        page,
                        size: QUICK_REFERENCE_PAGE_SIZE,
                    },
                    signal: controller.signal,
                });

                if (controller.signal.aborted) {
                    return;
                }

                if (!response.success) {
                    dispatch({ type: 'failure', publicationType });
                    return;
                }

                dispatch({
                    type: 'success',
                    publicationType,
                    documents: response.data.documents ?? [],
                    page,
                    hasNext: response.data.hasNext,
                    totalElements: response.data.totalElements,
                    append,
                });
            } catch (e) {
                if (controller.signal.aborted) {
                    return;
                }
                logger.error(`Failed to fetch quick references [${publicationType}]`, { error: e, carlineId });
                dispatch({ type: 'failure', publicationType });
            } finally {
                if (requestsRef.current.get(publicationType) === controller) {
                    requestsRef.current.delete(publicationType);
                }
            }
        },
        [carlineId, modelYear]
    );

    const activeTab = tabs[activePublicationType];

    useEffect(() => {
        if (vehicleKey && activeTab.status === 'idle') {
            fetchPage(activePublicationType, 0, false);
        }
    }, [vehicleKey, activePublicationType, activeTab.status, fetchPage]);

    const loadMore = useCallback(() => {
        if (activeTab.status === 'ready' && activeTab.hasNext) {
            fetchPage(activePublicationType, activeTab.lastLoadedPage + 1, true);
        }
    }, [activeTab.status, activeTab.hasNext, activeTab.lastLoadedPage, activePublicationType, fetchPage]);

    const retry = useCallback(() => {
        fetchPage(activePublicationType, 0, false);
    }, [activePublicationType, fetchPage]);

    // Matched back by title and version, not position: results are createdAt DESC, so a
    // document published since the first load shifts every index.
    const resolveDownloadUrl = useCallback(
        async (index: number): Promise<string | null> => {
            const reference = tabs[activePublicationType].documents[index];
            if (!reference) {
                return null;
            }

            const isFresh = Date.now() - tabs[activePublicationType].fetchedAt < DOWNLOAD_URL_TTL_MS;
            if (isFresh || !carlineId || !modelYear) {
                return reference.downloadUrl;
            }

            const page = Math.floor(index / QUICK_REFERENCE_PAGE_SIZE);

            try {
                const response = await docsService.getQuickReferences({
                    queryParameters: {
                        publicationType: activePublicationType,
                        carlineId,
                        modelYear,
                        page,
                        size: QUICK_REFERENCE_PAGE_SIZE,
                    },
                });

                if (!response.success) {
                    return reference.downloadUrl;
                }

                const refreshed = (response.data.documents ?? []).find(
                    (candidate) =>
                        candidate.documentTitle === reference.documentTitle && candidate.version === reference.version
                );

                if (!refreshed) {
                    return reference.downloadUrl;
                }

                dispatch({
                    type: 'refreshUrl',
                    publicationType: activePublicationType,
                    index,
                    downloadUrl: refreshed.downloadUrl,
                });

                return refreshed.downloadUrl;
            } catch (e) {
                logger.error(`Failed to refresh a quick reference download url [${activePublicationType}]`, {
                    error: e,
                });
                return reference.downloadUrl;
            }
        },
        [tabs, activePublicationType, carlineId, modelYear]
    );

    return useMemo(
        () => ({ tab: activeTab, loadMore, retry, resolveDownloadUrl }),
        [activeTab, loadMore, retry, resolveDownloadUrl]
    );
};
